import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { OFFICE } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENGAGEMENTS = ["Permanent", "Contract", "Contingent"];

const initial = { name: "", organisation: "", email: "", phone: "", role: "", engagement_type: "Permanent", skills: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.role) {
      toast.error("Please complete your name, work email and the role to fill.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We reply from our Sydney office.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-blue-600/70";

  return (
    <>
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 grid-bg" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <Reveal>
            <Eyebrow testId="contact-eyebrow">Let's build what's next</Eyebrow>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#0A1F44] max-w-3xl leading-[1.08]">
              Your next hire starts with <span className="text-blue-700">a conversation.</span>
            </h1>
            <p className="mt-7 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
              Tell us which role you need to fill, your preferred engagement type and the skills you
              are looking for.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-[1fr_1.3fr] gap-14 items-start">
        <Reveal className="space-y-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-7 space-y-6" data-testid="contact-details-card">
            <div className="flex gap-4">
              <Mail className="h-5 w-5 text-blue-700 mt-0.5" />
              <div>
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-slate-500">Email</p>
                <a href={`mailto:${OFFICE.email}`} data-testid="contact-email-link" className="text-slate-800 hover:text-blue-700 transition-colors duration-200">
                  {OFFICE.email}
                </a>
              </div>
            </div>
            <div className="flex gap-4">
              <Phone className="h-5 w-5 text-blue-700 mt-0.5" />
              <div className="space-y-2">
                <div>
                  <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-slate-500">Australia</p>
                  <p className="text-slate-800">{OFFICE.phoneAU}</p>
                </div>
                <div>
                  <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-slate-500">New Zealand</p>
                  <p className="text-slate-800">{OFFICE.phoneNZ}</p>
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <MapPin className="h-5 w-5 text-blue-700 mt-0.5" />
              <div>
                <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-slate-500">Sydney headquarters</p>
                {OFFICE.address.map((l) => (
                  <p key={l} className="text-slate-800">{l}</p>
                ))}
                <a
                  href={OFFICE.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-map-link"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-blue-700 hover:text-blue-600 transition-colors duration-200"
                >
                  View office location <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
          <p className="font-mono text-[0.68rem] uppercase tracking-[0.2em] text-slate-600">
            We reply to enquiries from our Sydney office.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-[#F4F7FD] p-7 md:p-9 space-y-5" data-testid="enquiry-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm text-slate-700 mb-2">Your name *</label>
                <input data-testid="enquiry-name-input" className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
              </div>
              <div>
                <label className="block text-sm text-slate-700 mb-2">Organisation</label>
                <input data-testid="enquiry-organisation-input" className={inputCls} value={form.organisation} onChange={set("organisation")} placeholder="Company name" />
              </div>
              <div>
                <label className="block text-sm text-slate-700 mb-2">Work email *</label>
                <input data-testid="enquiry-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
              </div>
              <div>
                <label className="block text-sm text-slate-700 mb-2">Phone</label>
                <input data-testid="enquiry-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+61 ..." />
              </div>
            </div>
            <div>
              <label className="block text-sm text-slate-700 mb-2">Role to fill *</label>
              <input data-testid="enquiry-role-input" className={inputCls} value={form.role} onChange={set("role")} placeholder="e.g. Cloud DevOps Engineer" />
            </div>
            <div>
              <label className="block text-sm text-slate-700 mb-2">Engagement type</label>
              <div className="flex flex-wrap gap-2">
                {ENGAGEMENTS.map((t) => (
                  <button
                    type="button"
                    key={t}
                    data-testid={`enquiry-engagement-${t.toLowerCase()}`}
                    onClick={() => setForm((f) => ({ ...f, engagement_type: t }))}
                    className={`rounded-full px-5 py-2.5 text-sm transition-all duration-200 border ${
                      form.engagement_type === t
                        ? "bg-amber-400 text-[#0A1F44] border-amber-400 font-semibold"
                        : "border-slate-300 text-slate-600 hover:border-blue-600/60 hover:text-blue-700"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-slate-700 mb-2">Skills and requirements</label>
              <textarea
                data-testid="enquiry-skills-input"
                rows={5}
                className={inputCls}
                value={form.skills}
                onChange={set("skills")}
                placeholder="Essential skills, working arrangements, start date..."
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-amber-400 text-[#0A1F44] font-semibold px-8 py-3.5 transition-transform duration-200 hover:scale-[1.03] active:scale-95 disabled:opacity-60"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {sending ? "Sending..." : "Prepare enquiry"}
              {!sending && (
                <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              )}
            </button>
          </form>
        </Reveal>
      </section>
    </>
  );
}
